import { cloneMap } from '../models/dto/dto.js';
import { SeatObjectState } from '../models/serializable/seating-state/seat-object-state.js';
import { SeatStateStatistics } from '../models/serializable/seating-state/seat-state-statistics.js';
import { isNotNullOrUndefined, isNullOrUndefined } from './null-safety.js';

export class SeatStateStatisticsHelper {
  static getSeatStateCount({
    statistics = null,
    state = null,
  } : {
    statistics? : SeatStateStatistics | null,
    state?: SeatObjectState | null,
  }) : number | null {
    if(isNullOrUndefined(statistics) || isNullOrUndefined(state)) {
      return null;
    }

    return statistics.statistics.get(state) ?? null;
  }

  static getSeatStateCountOrZero({
    statistics = null,
    state = null,
  } : {
    statistics? : SeatStateStatistics | null,
    state?: SeatObjectState | null,
   }) : number {
    return SeatStateStatisticsHelper.getSeatStateCount({ statistics: statistics, state: state }) ?? 0;
  }

  static sumSeatStateCount({
    statistics = null,
    states = null,
  } : {
    statistics? : SeatStateStatistics | null,
    states?: SeatObjectState[] | null,
  }) : number {
    let total : number = 0;
    if(isNullOrUndefined(statistics) || isNullOrUndefined(states) || states.length === 0) {
      return total;
    }

    for(const state of states) {
      total += SeatStateStatisticsHelper.getSeatStateCountOrZero({ statistics: statistics, state: state });
    }

    return total;
  }

  static tryChangeSeatCount({
    statistics = null,
    seatCount = 0,
    updateFunc = null,
  }: {
      statistics? : SeatStateStatistics | null,
      seatCount : number,
      updateFunc?: ((statistics: Map<SeatObjectState, number>) => Map<SeatObjectState, number>) | null
  }) : SeatStateStatistics | null {
    if(isNullOrUndefined(statistics)) {
      return null;
    }

    if(seatCount == 0) {
      return SeatStateStatistics.empty();
    }

    let statisticsMap: Map<SeatObjectState, number> | null | undefined = undefined;
    if(isNotNullOrUndefined(updateFunc)) {
      statisticsMap = updateFunc(statistics.statistics);
    }

    return SeatStateStatisticsHelper.validate({
      statistics: SeatStateStatisticsHelper.trim({
        statistics: statistics.copyWith({
          seatCount: seatCount,
          statistics: statisticsMap,
        }),
      } ),
    });
  }

  static tryChangeSeatState({
    statistics = null,
    fromState = null,
    toState = null,
    swap: swap = 1,
  } : {
      statistics? : SeatStateStatistics | null,
      fromState? : SeatObjectState | null,
      toState? : SeatObjectState | null,
      swap : number,
  }) : SeatStateStatistics | null {
    if(isNullOrUndefined(statistics) || isNullOrUndefined(fromState) || isNullOrUndefined(toState) || swap === 0) {
      return statistics?.clone() ?? null;
    }

    const statisticsMap = cloneMap(statistics.statistics);
    statisticsMap.set(fromState, SeatStateStatisticsHelper.getSeatStateCountOrZero({ statistics, state: fromState }) - swap);
    statisticsMap.set(toState, SeatStateStatisticsHelper.getSeatStateCountOrZero({ statistics, state: toState }) + swap);

    return SeatStateStatisticsHelper.validate({
      statistics: SeatStateStatisticsHelper.trim({
        statistics: statistics.copyWith({ statistics: statisticsMap }),
      }),
    });
  }

  static trim({
    statistics = null,
  } : {
    statistics? : SeatStateStatistics | null,
  }): SeatStateStatistics | null {
    if(isNullOrUndefined(statistics)) {
      return null;
    }

    if(statistics.seatCount === 0) {
      return SeatStateStatistics.empty();
    }

    const statisticsMap = new Map<SeatObjectState, number>();

    for(const entry of statistics.statistics.entries()) {
      if(entry[1] !== 0) {
        statisticsMap.set(entry[0], entry[1]);
      }
    }

    return statistics.copyWith({ statistics: statisticsMap });
  }

  static validate({
    statistics = null,
  } : {
    statistics? : SeatStateStatistics | null,
  }): SeatStateStatistics | null {
    if(isNullOrUndefined(statistics)) {
      return null;
    }

    if(statistics.seatCount < 0) {
      return null;
    }

    let seatCount: number = 0;
    for(const value of statistics.statistics.values()) {
      if(value < 0) {
        return null;
      }

      seatCount += value;
    }

    if(seatCount !== statistics.seatCount) {
      return null;
    }

    return statistics;
  }
}
